import { useContext, useEffect, useState, useRef } from 'react';
import { getReportsV2, getStatePersonalizedV2 } from '../../../helpers/api';
import PageContent from '../../common/PageContent';
import { AppContext } from '../../../store/AppContext';

// Standard Analytics Components
import StatCardsRegulatorView from './regulator/StatCardsRegulatorView';
import SectorAnalyticsRegulatorView from './regulator/SectorAnalyticsRegulatorView';
import CategoryAnalyticsRegulatorView from './regulator/CategoryAnalyticsRegulatorView';

// Re-factored Upgraded Sub-Widgets
import ReportStatusBreakdown from './regulator/ReportStatusBreakdown';
import UncreatedSchedulesAlert from './regulator/UncreatedSchedulesAlert';
import FacilitySamplingLeaderboard from './regulator/FacilitySamplingLeaderboard';

// MUI Core Component Imports
import {
  Box,
  CircularProgress,
  Grid2,
  TextField,
  Typography,
  Autocomplete,
  MenuItem,
  Select,
  Stack,
  LinearProgress,
  alpha,
  Button,
} from '@mui/material';
import { CalendarTodayOutlined } from '@mui/icons-material';
import { getYears } from '../../../helpers/dateHelpers';
import { useHistory } from 'react-router-dom';

export default function RegulatorDashboard() {
  const history = useHistory(); // Initialize history

  const { state } = useContext(AppContext);
  const loggedInFirstName = state?.auth?.result?.data?.firstname;

  // --- YEAR FILTER SETUP ---
  const years = getYears();
  const currentYear = new Date().getFullYear()?.toString();
  const [selectedYear, setSelectedYear] = useState(currentYear);

  const initState = {
    stateId: '',
    stateName: '',
    coordinates: {
      latitude: '',
      longitude: '',
    },
  };

  const [stateList, setStateList] = useState([]);
  const [stateLoading, setStateLoading] = useState(false);
  const [stateSelected, setStateSelected] = useState(initState);
  const [resultList, setResultList] = useState([]);
  const [resultLoading, setResultLoading] = useState(false);

  // Anchor Ref to track the section that updates based on the Year parameter
  const yearComponentsRef = useRef(null);
  // Track initial render to prevent scrolling on page mount
  const isFirstRender = useRef(true);

  // Fetch geographical region dropdown options on mount
  useEffect(() => {
    fetchStateList();
  }, []);

  // Poll the API immediately whenever either stateId or selectedYear changes
  useEffect(() => {
    fetchResultList();
  }, [stateSelected?.stateId, selectedYear]);

  // Precise controlled smooth scroll with an increased gap to safely clear the fixed layout panel
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (yearComponentsRef.current) {
      const elementPosition = yearComponentsRef.current.getBoundingClientRect().top;
      // Increased offset padding subtraction to 210px to account for both MUI Top Appbar and Fixed Filters Bar
      const offsetPosition = elementPosition + window.pageYOffset - 210;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  }, [selectedYear]);

  const fetchStateList = async () => {
    setStateLoading(true);
    try {
      const res = await getStatePersonalizedV2();
      const { data, status } = res?.result;
      if (status?.code === '00') {
        setStateList(data || []);
      }
    } catch (error) {
      console.error('Error fetching structural personalized state list:', error);
    } finally {
      setStateLoading(false);
    }
  };

  const fetchResultList = async () => {
    setResultLoading(true);
    try {
      const res = await getReportsV2(
        '',
        '',
        stateSelected?.stateId || '',
        '',
        selectedYear,
        '',
        '',
        0,
        1,
        1000
      );
      const { data, status } = res?.result;
      if (status?.code === '00') {
        setResultList(data || []);
      } else {
        setResultList([]);
      }
    } catch (error) {
      console.error('Error fetching dynamic monitoring results:', error);
      setResultList([]);
    } finally {
      setResultLoading(false);
    }
  };

  const renderFilterControls = () => {
    return (
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        width={{ xs: '100%', sm: 'auto' }}
        alignItems="center"
      >
        {/* <Button
          sx={{
            textTransform: 'none',
          }}
          endIcon={
            <Box
              component="span"
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: '#ff1744',
                display: 'inline-block',
                boxShadow: '0 0 8px #ff1744',
                animation: 'blink 1.5s infinite ease-in-out',
                '@keyframes blink': {
                  '0%, 100%': {
                    opacity: 1,
                    transform: 'scale(1)',
                  },
                  '50%': {
                    opacity: 0.3,
                    transform: 'scale(0.8)',
                  },
                },
              }}
            />
          }
          onClick={() => {
            history.push('/map/live/');
          }}
        >
          Live
        </Button> */}

        <Select
          value={selectedYear}
          size="small"
          onChange={(e) => setSelectedYear(e.target.value)}
          startAdornment={
            <CalendarTodayOutlined sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
          }
          sx={{
            borderRadius: 3,
            minWidth: 120,
            fontWeight: 600,
            bgcolor: 'background.paper',
            width: { xs: '100%', sm: 'auto' },
          }}
        >
          {years?.map((y) => (
            <MenuItem key={y} value={y}>
              {y}
            </MenuItem>
          ))}
        </Select>

        {/* GEOGRAPHICAL REGION AUTOCOMPLETE CONTROL */}
        <Autocomplete
          disablePortal
          value={stateSelected?.stateId ? stateSelected : null}
          options={stateList}
          getOptionLabel={(option) => option.stateName || ''}
          onChange={(event, newValue) => {
            setStateSelected(newValue || initState);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="All States"
              size="small"
              InputProps={{
                ...params.InputProps,
                startAdornment: stateLoading && <CircularProgress size={16} sx={{ mr: 1 }} />,
                sx: {
                  borderRadius: 3,
                  bgcolor: 'background.paper',
                },
              }}
            />
          )}
          renderOption={(props, option) => (
            <MenuItem {...props} key={option.stateId}>
              <Typography variant="body2">{option.stateName}</Typography>
            </MenuItem>
          )}
          sx={{ width: { xs: '100%', sm: 250 } }}
        />
      </Stack>
    );
  };

  return (
    <PageContent>
      <Box sx={{ p: { xs: 2, md: 2 }, bgcolor: '#F4F6F8', minHeight: '100vh', mt: -1 }}>
        {/* FIXED HEADER WELCOME & GLOBAL CONTROL ROW */}
        <Box
          sx={{
            position: 'fixed',
            top: { xs: '56px', sm: '64px' },
            left: { xs: 0, md: '280px' },
            right: 0,
            zIndex: 1100,
            bgcolor: '#F4F6F8',
            px: { xs: 2, md: 1 },
            pt: 2,
            pb: 2,
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', md: 'center' },
            gap: 2,
            borderBottom: '1px solid',
            borderColor: alpha('#000', 0.05),
            boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
          }}
        >
          <Box>
            <Typography variant="h4" fontWeight={700} color="text.primary" sx={{ mb: 0.5 }}>
              Hi, {loggedInFirstName} 👋
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Welcome to your operational overview workspace.
            </Typography>
          </Box>
          {renderFilterControls()}
        </Box>

        <Box sx={{ height: { xs: '140px', md: '90px' }, mb: 2 }} />

        <Box mb={4}>
          <StatCardsRegulatorView stateId={stateSelected?.stateId} />
        </Box>

        <Grid2 container spacing={3} mb={4}>
          <Grid2 size={{ lg: 6, md: 12, xs: 12 }}>
            <CategoryAnalyticsRegulatorView stateId={stateSelected?.stateId} />
          </Grid2>
          <Grid2 size={{ lg: 6, md: 12, xs: 12 }}>
            <SectorAnalyticsRegulatorView stateId={stateSelected?.stateId} />
          </Grid2>
        </Grid2>

        <Box ref={yearComponentsRef} sx={{ position: 'relative', pt: 1 }}>
          {resultLoading && (
            <LinearProgress
              sx={{
                position: 'absolute',
                top: -12,
                left: 0,
                right: 0,
                height: 4,
                borderRadius: 2,
                zIndex: 10,
              }}
            />
          )}

          <Box
            sx={{
              transition: 'opacity 0.3s ease',
              opacity: resultLoading ? 0.65 : 1,
              pointerEvents: resultLoading ? 'none' : 'auto',
            }}
          >
            <Grid2 container spacing={3} mb={4} alignItems="stretch">
              <Grid2 size={{ lg: 7, md: 12, xs: 12 }}>
                <UncreatedSchedulesAlert
                  reportData={resultList}
                  stateId={stateSelected?.stateId}
                  loading={resultLoading}
                />
              </Grid2>
              <Grid2 size={{ lg: 5, md: 12, xs: 12 }}>
                <ReportStatusBreakdown
                  reportData={resultList}
                  stateId={stateSelected?.stateId}
                  loading={resultLoading}
                />
              </Grid2>
            </Grid2>

            <Grid2 container spacing={3} mb={12}>
              <Grid2 size={{ lg: 12, md: 12, xs: 12 }}>
                <FacilitySamplingLeaderboard
                  reportData={resultList}
                  stateId={stateSelected?.stateId}
                  loading={resultLoading}
                />
              </Grid2>
            </Grid2>
          </Box>
        </Box>
      </Box>
    </PageContent>
  );
}
